import { useId, useState } from 'preact/hooks'
import { Search, X } from 'lucide-preact'
import type { LandingPageTemplateListItemDto } from '@/types/landingPage'
import { BRAND_CLASSES } from '@/constants/theme'
import { GHOST_SMALL, SAVE_SMALL } from '@/components/knowledgeBase/brandGuidelines/brandGuidelinesStyles'
import { TemplatePreviewPane } from '@/components/landingPageTemplates/TemplatePreviewPane'
import { matchesTemplateSearch, signedOffText } from '@/components/landingPageTemplates/templateText'
import { useDialogFocus } from '@/components/landingPageTemplates/useDialogFocus'

interface TemplateChooserProps {
    workspaceId: string
    /** Every template this web can start from, newest first. */
    templates: LandingPageTemplateListItemDto[]
    /** The template already picked, which the chooser opens on; else it opens on the newest. */
    initialWebId: string | null
    compatibleLabel: string
    onUse: (template: LandingPageTemplateListItemDto) => void
    onClose: () => void
}

/**
 * Every template this web can start from, with search and the same full preview as the Templates page (ORB-1103).
 * It covers the builder until a template is used or the chooser is closed; closing it changes nothing.
 */
export function TemplateChooser({ workspaceId, templates, initialWebId, compatibleLabel, onUse, onClose }: TemplateChooserProps) {
    const titleId = useId()
    const searchId = useId()
    const { dialogRef, initialFocusRef } = useDialogFocus<HTMLInputElement>(onClose)
    const [search, setSearch] = useState('')
    const [currentWebId, setCurrentWebId] = useState<string | null>(
        templates.some(template => template.WebId === initialWebId) ? initialWebId : templates[0]?.WebId ?? null,
    )

    const matches = templates.filter(template => matchesTemplateSearch(template, search))
    // A search that hides the template being looked at moves to the first one it still shows.
    const current = matches.find(template => template.WebId === currentWebId) ?? matches[0] ?? null

    return (
        <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className="fixed inset-0 z-[60] flex flex-col bg-white dark:bg-slate-900 focus:outline-none"
        >
            <div className="shrink-0 flex items-center justify-between gap-4 px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
                <p className="min-w-0 flex items-baseline gap-2">
                    <span id={titleId} className="text-sm font-medium text-slate-700 dark:text-slate-200">Choose a template</span>
                    <span className="truncate text-xs text-slate-500 dark:text-slate-400">From {compatibleLabel} webs, newest first</span>
                </p>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="shrink-0 p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                    <X className="w-5 h-5" aria-hidden="true" />
                </button>
            </div>

            <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[340px_minmax(0,1fr)]">
                <div className="min-h-0 flex flex-col border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700">
                    <div className="relative shrink-0 mx-4 mt-4 mb-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" aria-hidden="true" />
                        <label htmlFor={searchId} className="sr-only">Search templates</label>
                        <input
                            ref={initialFocusRef}
                            id={searchId}
                            type="search"
                            value={search}
                            autoComplete="off"
                            placeholder="Search templates"
                            onInput={event => setSearch((event.target as HTMLInputElement).value)}
                            className={`w-full text-sm pl-8 pr-3 py-2 rounded-md border ${BRAND_CLASSES.INPUT.NORMAL} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                        />
                    </div>
                    {matches.length === 0 && (
                        <p className="px-7 pt-2 text-sm text-slate-500 dark:text-slate-400">No templates match “{search.trim()}”.</p>
                    )}
                    <ul className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-0.5 px-2 pt-1 pb-4 max-h-48 md:max-h-none">
                        {matches.map(template => {
                            const isCurrent = template.WebId === current?.WebId
                            return (
                                <li key={template.WebId}>
                                    <button
                                        type="button"
                                        aria-pressed={isCurrent}
                                        onClick={() => setCurrentWebId(template.WebId)}
                                        className={`w-full flex flex-col px-3 py-2.5 rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                                            isCurrent ? 'bg-primary-50 dark:bg-primary-950/40' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                                        }`}
                                    >
                                        <span className={`text-sm font-medium ${isCurrent ? 'text-primary-700 dark:text-primary-300' : 'text-slate-800 dark:text-slate-100'}`}>
                                            {template.Template.Name}
                                        </span>
                                        <span className="text-xs text-slate-500 dark:text-slate-400">
                                            {template.CampaignName}
                                            {template.LatestSignedOffVersion && ` · ${signedOffText(template.LatestSignedOffVersion, false)}`}
                                        </span>
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                </div>

                <div className="min-w-0 min-h-0 flex flex-col">
                    {current && <TemplatePreviewPane workspaceId={workspaceId} template={current} />}
                </div>
            </div>

            <div className="shrink-0 flex items-center justify-between gap-4 px-4 py-3 border-t border-slate-200 dark:border-slate-700">
                <p className="min-w-0 flex items-baseline gap-2">
                    {current && (
                        <>
                            <span className="truncate text-sm font-medium text-slate-900 dark:text-white">{current.Template.Name}</span>
                            {current.LatestSignedOffVersion && (
                                <span className="hidden sm:inline whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
                                    {signedOffText(current.LatestSignedOffVersion)}
                                </span>
                            )}
                        </>
                    )}
                </p>
                <span className="shrink-0 flex gap-2">
                    <button type="button" className={GHOST_SMALL} onClick={onClose}>Cancel</button>
                    <button type="button" className={SAVE_SMALL} disabled={!current} onClick={() => current && onUse(current)}>
                        Use this template
                    </button>
                </span>
            </div>
        </div>
    )
}
